import { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import InlineSVG from "react-inlinesvg";
import { Spinner } from "src/sudokit/components/Spinner";
import ProjectModel from "../../../models/Project";
import { useProjectStore } from "../../../stores/projectStore";
import { useWebSocket } from "../../SocketHost/WebSocketProvider";
import { IMPORT_WIP_STEPS } from "../../../../../src/shared/constants";

const Container = styled.div`
  position: fixed;
  z-index: 10050;
  bottom: 16px;
  max-width: min(360px, calc(100vw - 32px));
  background: var(--menu-color);
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--grey-3) 55%, transparent);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
  padding: 16px 18px 14px;
  gap: 10px;

  .header-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .import-icon-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    svg path {
      stroke: var(--grey-1);
      fill: none;
    }
  }

  .title-import {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--grey-1);
    line-height: 130%;
    flex: 1;
    min-width: 0;
  }

  .spinner-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-text {
    font-size: 14px;
    color: var(--grey-2);
    line-height: 140%;
    margin: 0;
  }

  .progress-track {
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--grey-3) 40%, transparent);
    overflow: hidden;
    margin-top: 2px;
  }

  .progress-fill {
    height: 100%;
    background: var(--primary-cta);
    transition: width 400ms ease;
  }

  .footnote {
    font-size: 12px;
    font-style: italic;
    color: var(--grey-2);
    line-height: 140%;
    margin: 4px 0 0;
  }

  .snippet-feed {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid color-mix(in srgb, var(--grey-3) 35%, transparent);
    max-height: 6.5em;
    overflow: hidden;
  }

  .snippet-row {
    font-size: 12px;
    line-height: 135%;
    color: var(--grey-2);
    min-width: 0;
  }

  .snippet-kind {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 10px;
    color: var(--grey-1);
  }

  .snippet-title {
    font-weight: 600;
    color: var(--grey-1);
  }

  .snippet-preview {
    display: block;
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Ordered step sequence we want to show progress for. CANCELLED is excluded because
// it isn't really a forward step. TRAINING_MY_VOICE is excluded when the user did
// not opt into My Voice training (mirrors the logic in ProjectCard).
const DOCUMENT_STEPS = ["CREATING", "TRAINING_MY_VOICE", "INITIAL_ANALYSIS", "OUTLINE", "STORY_BIBLE_ENRICHMENT"];
const SCRIVENER_STEPS = ["EXTRACTING", "PARSING", "IMPORTING_DOCUMENTS"];

function getActiveSteps({ importSource, hasMyVoiceTraining }) {
  if (importSource === "scrivener") return SCRIVENER_STEPS;
  return hasMyVoiceTraining ? DOCUMENT_STEPS : DOCUMENT_STEPS.filter((s) => s !== "TRAINING_MY_VOICE");
}

const SNIPPET_KIND_LABEL = {
  character: "Character",
  worldbuilding: "Worldbuilding",
  genre: "Genre",
  chapters: "Chapters",
  outline: "Outline",
  synopsis: "Synopsis",
  import_report: "Report",
};

const SNIPPET_FIRST_REVEAL_MS = 220;
const SNIPPET_BETWEEN_REVEALS_MS = 5000;

/**
 * Bottom-right of the editor column (left of the chat / right bar), driven by Firestore
 * `importStep` (same labels as the dashboard card). Shown after the user enters the project
 * post–Import Report; hides when `importStep` clears (SW-9080).
 *
 * Card layout matches agentic chat content cards (e.g. Plan): titled header, progress bar, no
 * redundant step counter (homepage import already showed phase steps).
 */
export default function ImportProgressBanner({ projectId, initialImportStep, initialImportSource, initialHasMyVoiceTraining }) {
  const document = useProjectStore((state) => state.document);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const [importStep, setImportStep] = useState(initialImportStep || null);
  const [importSource, setImportSource] = useState(initialImportSource || null);
  const [hasMyVoiceTraining, setHasMyVoiceTraining] = useState(!!initialHasMyVoiceTraining);
  const [snippets, setSnippets] = useState([]);
  const firebaseProjectRef = useRef(null);
  const importStepRef = useRef(initialImportStep || null);
  const snippetQueueRef = useRef([]);
  const snippetRevealTimerRef = useRef(null);
  const { registerHandler, unregisterHandler } = useWebSocket();

  // When the manuscript editor is open, sit above the editor pane with the right bar to our right.
  const rightInsetPx = (document ? columnWidths.right.current : 0) + 16;

  useEffect(() => {
    importStepRef.current = importStep;
  }, [importStep]);

  useEffect(() => {
    if (!importStep || importStep === "CANCELLED") {
      if (snippetRevealTimerRef.current != null) {
        clearTimeout(snippetRevealTimerRef.current);
        snippetRevealTimerRef.current = null;
      }
      snippetQueueRef.current = [];
      setSnippets([]);
    }
  }, [importStep]);

  useEffect(() => {
    if (!projectId) return undefined;

    const revealNext = () => {
      snippetRevealTimerRef.current = null;
      const row = snippetQueueRef.current.shift();
      if (!row) return;
      setSnippets((prev) => [row, ...prev].slice(0, 4));
      if (snippetQueueRef.current.length > 0) {
        snippetRevealTimerRef.current = setTimeout(revealNext, SNIPPET_BETWEEN_REVEALS_MS);
      }
    };

    const handler = (msg) => {
      const payload = msg?.payload;
      if (!payload || payload.projectId !== projectId) return;
      const step = importStepRef.current;
      if (!step || step === "CANCELLED") return;
      const kind = payload.kind;
      if (!SNIPPET_KIND_LABEL[kind]) return;
      const name = typeof payload.name === "string" ? payload.name.trim() : "";
      if (!name) return;

      const row = {
        id: `${kind}:${name}:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`,
        kind,
        name,
        preview: typeof payload.preview === "string" ? payload.preview : "",
      };
      snippetQueueRef.current.push(row);
      if (snippetRevealTimerRef.current == null) {
        snippetRevealTimerRef.current = setTimeout(revealNext, SNIPPET_FIRST_REVEAL_MS);
      }
    };

    registerHandler("importKnowledgeSnippet", handler);
    return () => {
      unregisterHandler("importKnowledgeSnippet");
      if (snippetRevealTimerRef.current != null) {
        clearTimeout(snippetRevealTimerRef.current);
        snippetRevealTimerRef.current = null;
      }
      snippetQueueRef.current = [];
    };
  }, [projectId, registerHandler, unregisterHandler]);

  useEffect(() => {
    if (!projectId) return undefined;

    let released = false;
    ProjectModel.get(projectId).then((proj) => {
      if (released || !proj) return;
      firebaseProjectRef.current = proj;
      setImportStep(proj.importStep);
      setImportSource(proj.importSource);
      setHasMyVoiceTraining(proj.hasMyVoiceTraining);
      proj.subscribe((updated) => {
        setImportStep(updated.importStep);
        setImportSource(updated.importSource);
        setHasMyVoiceTraining(updated.hasMyVoiceTraining);
      });
    });

    return () => {
      released = true;
      firebaseProjectRef.current?.release();
      firebaseProjectRef.current = null;
    };
  }, [projectId]);

  if (!importStep || importStep === "CANCELLED") return null;

  const activeSteps = getActiveSteps({ importSource, hasMyVoiceTraining });
  const currentIndex = activeSteps.indexOf(importStep);
  // Fallback when the current step isn't in the list we expected (e.g. an unknown step arrives):
  // treat as in-progress but don't show a precise step counter.
  const hasIndex = currentIndex >= 0;
  const totalSteps = activeSteps.length;
  // Represent progress as finished-step fraction; e.g. on step 2/5 we show ~20% filled (step 1
  // already completed, step 2 in progress). Clamp to [5%, 95%] so the bar never looks empty
  // or prematurely done.
  const progress = hasIndex ? Math.min(0.95, Math.max(0.05, currentIndex / totalSteps)) : 0.1;

  const statusText = IMPORT_WIP_STEPS[importStep] || "Importing...";

  return (
    <Container
      className="import-progress-banner"
      style={{ right: rightInsetPx }}
      role="status"
      aria-live="polite"
      aria-label="Import in progress"
    >
      <div className="header-row">
        <span className="import-icon-wrap" aria-hidden="true">
          <InlineSVG
            src="/assets/import.svg"
            className="w-5 h-5"
          />
        </span>
        <span className="title-import">Import</span>
        <span className="spinner-wrap" aria-hidden="true">
          <Spinner size="sm" />
        </span>
      </div>
      <p className="status-text">{statusText}</p>
      <div
        className="progress-track"
        aria-hidden="true"
      >
        <div
          className="progress-fill"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      {snippets.length > 0 && (
        <div
          className="snippet-feed"
          aria-live="polite"
          aria-label="Story Bible discoveries from your import"
        >
          {snippets.slice(0, 2).map((s) => (
            <div
              key={s.id}
              className="snippet-row"
            >
              <span className="snippet-kind">{SNIPPET_KIND_LABEL[s.kind]}</span>
              {" · "}
              <span className="snippet-title">{s.name}</span>
              {s.preview ? <span className="snippet-preview">{s.preview}</span> : null}
            </div>
          ))}
        </div>
      )}
      <p className="footnote">This will take a few minutes.</p>
    </Container>
  );
}
